import { ComponentType, useRef, useCallback, useMemo, useState } from 'react';

import { useModal } from 'mui-modal-provider';

import { DialogWrapper } from './_components/DialogWrapper';
import { CloseDialogFn, DialogResult } from './types';

type ModalOutput = ReturnType<typeof useModal>;
type ShowModalFn = ModalOutput['showModal'];

export type ShowModalOutput = ReturnType<ShowModalFn>;

type MakeDialogArgs = {
  // Should destroy dialog when component that opened it unmounts
  disableAutoDestroy?: boolean;
  // Should close dialog when overlay was clicked
  closeOnOverlayClick?: boolean;
};

type DialogHookArgs<RESULT = unknown> = {
  onClose?: CloseDialogFn<RESULT>;
};

type OpenDialogOptions<RESULT = unknown> = DialogHookArgs<RESULT>;

/* eslint-disable @typescript-eslint/no-empty-object-type */
type OpenDialogFn<PROPS, RESULT> = keyof PROPS extends never
  ? (props?: undefined, opts?: OpenDialogOptions<RESULT>) => void
  : {} extends PROPS
  ? (props?: PROPS, opts?: OpenDialogOptions<RESULT>) => void
  : (props: PROPS, opts?: OpenDialogOptions<RESULT>) => void;
/* eslint-enable @typescript-eslint/no-empty-object-type */

type UseDialog<PROPS, RESULT> = {
  // open the dialog with the give props
  openDialog: OpenDialogFn<PROPS, RESULT>;
  // destroy the dialog
  destroy: () => void;
  // update the dialog props
  update: (props: Partial<PROPS>) => void;
  // result of the dialog, after closing the dialog
  result?: DialogResult<RESULT>;
};

/**
 * Creates a reusable hook for opening a dialog with the given component.
 *
 * The returned hook provides an `openDialog` function that renders the component
 *
 * @param component - The dialog content component to render.
 * @param makeDialogArgs - Optional configuration for dialog behavior.
 * @param makeDialogArgs.disableAutoDestroy - Whether to keep the dialog alive when the calling component unmounts. Defaults to `true`.
 * @param makeDialogArgs.closeOnOverlayClick - Whether clicking the overlay closes the dialog.
 * @returns A React hook that returns `{ openDialog }`.
 */
export const makeDialog =
  <PROPS, RESULT = unknown>(
    component: ComponentType<PROPS>,
    { disableAutoDestroy = true, closeOnOverlayClick }: MakeDialogArgs = {}
  ) =>
  (hookArgs?: DialogHookArgs<RESULT>): UseDialog<PROPS, RESULT> => {
    const modalRef = useRef<ShowModalOutput>(null);
    const { showModal } = useModal({ disableAutoDestroy });
    const prevProps = useRef<PROPS>(undefined);
    const hookArgsRef = useRef<DialogHookArgs<RESULT>>(hookArgs);
    hookArgsRef.current = hookArgs;

    const [result, setResult] = useState<DialogResult<RESULT> | undefined>(
      undefined
    );

    const openDialog = useCallback(
      (props: PROPS, opts?: OpenDialogOptions<RESULT>) => {
        let modal: ShowModalOutput | undefined = undefined;
        // Reset the previous result when dialog is opened
        setResult(undefined);

        const destroyCurrent: CloseDialogFn<RESULT> = (result) => {
          modal?.destroy();
          opts?.onClose?.(result);
          hookArgsRef.current?.onClose?.(result);
          prevProps.current = undefined;
          setResult(result);
        };
        prevProps.current = props;
        modal = showModal(DialogWrapper<PROPS, RESULT>, {
          destroy: destroyCurrent,
          DialogComponent: component,
          dialogComponentProps: props,
          closeOnOverlayClick,
        });
        modalRef.current = modal;
      },
      [showModal]
    );

    return useMemo(
      () =>
        ({
          openDialog,
          destroy: () => {
            prevProps.current = undefined;
            modalRef.current?.destroy();
          },
          update: (props) => {
            const newProps = { ...prevProps.current, ...props };
            prevProps.current = newProps as PROPS;
            modalRef.current?.update({
              dialogComponentProps: newProps,
            });
          },
          result,
        } as UseDialog<PROPS, RESULT>),
      [openDialog, result]
    );
  };
